import { Component, Input, OnInit, ViewChild } from '@angular/core'
import {
  Channel,
  ChannelService,
  ConnectedChannelAccountResponse,
  Toaster,
  UnlinkedListing,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import * as R from 'ramda'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { defaultUndefinedRoomId } from '@tv3/store/connection/connection.model'
import {
  GetAirbnbListingStatus,
  LoadConnectionsByChannel,
  RefreshConnectionsForChannel,
} from '@tv3/store/connection/connection.actions'
import { selectChannelPropertyIds, selectConnectionViewsByChannelId } from '@tv3/store/connection/connection.selectors'
import { ConnectionDialogService } from '@tv3/containers/channels/connection-detail/connection-dialog.service'
import { AirbnbHelperService } from '@tv3/containers/channels/channel-connect/airbnb-helper.service'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { filter, map, take } from 'rxjs/operators'
import { ConnectionHelperService } from '@tv3/containers/channels/channel-connect/connection-helper.service'
import { combineLatest, Observable } from 'rxjs'
import { ChannelAccountsCacheService } from '../channel-accounts-cache.service'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { PropertyContentHelperService } from '@tv3/containers/channels/channel-connect/property-content-helper.service'
import { isTiketLikeChannel } from '@tokeet-frontend/channels'
import { hasChannelContentApi } from '@tv3/store/channel-content'
import { selectActiveTokeetPlanView } from '@tv3/store/plan/plan.selectors'
import { SelectableRow } from '@tokeet-frontend/tv3-platform'

import { UserStorage } from '@tokeet-frontend/tv3-platform'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { FormBuilder } from '@angular/forms'
import { AirbnbSetVisibilityDialogService } from '@tv3/containers/channels/connection-detail/airbnb-set-visibility/airbnb-set-visibility-dialog.service'
import { CreateAirbnbListingDialogService } from '@tv3/containers/channels/channel-connect/dialogs/create-airbnb-listing-dialog/create-airbnb-listing-dialog.service'

@Component({
  selector: 'app-channel-mapped-rentals',
  templateUrl: './channel-mapped-rentals.component.html',
  styleUrls: ['./channel-mapped-rentals.component.scss'],
})
export class ChannelMappedRentalsComponent extends SelectableRow<ConnectionView> implements OnInit {
  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = ['select', 'roomName', 'propertyId', 'roomId', 'linkDateOrCreated', 'rentalName', 'edit']

  @Input() channel: Channel
  isAirBnBV1 = false
  isAirBnBV2 = false
  isTiketLikeChannel = false
  hasChannelContentApi = false
  isEmptyTable$ = isEmptyTable(this.dataSource)
  tableType = TableType.MappedRentals
  isTokeetPaidSub$ = this.store.pipe(
    select(selectActiveTokeetPlanView),
    map((s) => s?.status === 'active' && s?.isPaid && !s?.isTrial)
  )

  propertyIds$: Observable<any[]>

  rowWarnings = new Map<string, { level: 'danger' | 'warning'; message: string }>()
  multiMappingRentalIds = new Set<string>()

  connections: ConnectionView[] = []
  filters = this.fb.group({
    linkage: [],
    propertyId: [],
    status: [],
  })

  constructor(
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private fb: FormBuilder,
    protected storage: UserStorage,
    private connectionDialog: ConnectionDialogService,
    private airbnbHelperService: AirbnbHelperService,
    private createAirbnbListingDialog: CreateAirbnbListingDialogService,
    private connectionHelperService: ConnectionHelperService,
    private propertyContentHelperService: PropertyContentHelperService,
    private airbnbSetVisibilityDialog: AirbnbSetVisibilityDialogService,
    private accountsCache: ChannelAccountsCacheService
  ) {
    super(storage)
  }

  ngOnInit() {
    this.hasChannelContentApi = hasChannelContentApi(this.channel)
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
    this.isAirBnBV2 = ChannelService.isAirBnBV2(this.channel.name)
    this.isAirBnBV1 = ChannelService.isAirBnBV1(this.channel.name)
    this.isTiketLikeChannel = isTiketLikeChannel(this.channel.name)

    if (this.channel.isConnected) {
      this.onRefresh()
    }

    this.propertyIds$ = this.store.pipe(select(selectChannelPropertyIds(this.channel.id)))
    const connections$ = this.store.pipe(select(selectConnectionViewsByChannelId, { id: this.channel.id }))
    const accounts$ = this.accountsCache.getAccounts(this.channel)

    combineLatest([accounts$, connections$])
      .pipe(untilDestroy(this))
      .subscribe(([accounts, connections]) => {
        this.rowWarnings = this.computeRowWarnings(accounts || [], connections)
        this.multiMappingRentalIds = this.computeMultiMappingRentalIds(connections)
      })

    connections$.pipe(untilDestroy(this)).subscribe((connections) => {
      this.connections = [...connections]
      this.filterData(this.filters.value)
    })

    // Refresh the Airbnb approval badge (Room ID column) with the live listing status —
    // the value persisted on the saved room only changes when rooms are re-saved.
    // take(1): the resulting store updates re-emit connections$, which must not re-dispatch.
    if (this.isAirBnBV2) {
      connections$
        .pipe(
          filter((connections) => !!connections?.length),
          take(1),
          untilDestroy(this)
        )
        .subscribe((connections) => {
          const items = connections
            .filter(
              (connection) => connection.propertyId && connection.roomId && connection.roomId !== defaultUndefinedRoomId
            )
            .map((connection) => ({ id: connection.id, propertyId: connection.propertyId, listing: connection.roomId }))
          if (items.length) {
            this.store.dispatch(GetAirbnbListingStatus({ items }))
          }
        })
    }

    this.filters.valueChanges.pipe(untilDestroy(this)).subscribe((filters) => {
      this.filterData(filters)
    })
  }

  protected isRowSelectable(row: ConnectionView) {
    return !row?.isFeedApi && !this.isAirbnbSyncPaused(row)
  }

  private computeRowWarnings(
    accounts: ConnectedChannelAccountResponse[],
    connections: ConnectionView[]
  ): Map<string, { level: 'danger' | 'warning'; message: string }> {
    const warnings = new Map<string, { level: 'danger' | 'warning'; message: string }>()

    const accountByPropertyId = new Map<string, ConnectedChannelAccountResponse>()
    const rentalToAccounts = new Map<string, ConnectedChannelAccountResponse[]>()
    const unlinkedByProperty = new Map<string, Map<string, UnlinkedListing>>()

    for (const acct of accounts) {
      accountByPropertyId.set(String(acct.propertyId), acct)
      for (const rental of acct.rentals || []) {
        const rid = (rental as any).tokeetRentalId
        if (!rid) continue
        if (!rentalToAccounts.has(rid)) rentalToAccounts.set(rid, [])
        rentalToAccounts.get(rid)!.push(acct)
      }
      const unlinked = new Map<string, UnlinkedListing>()
      for (const u of acct.unlinked_listings || []) {
        if (u?.listing_id) unlinked.set(String(u.listing_id), u)
      }
      unlinkedByProperty.set(String(acct.propertyId), unlinked)
    }

    for (const conn of connections) {
      if (!conn.id) continue
      const parent = accountByPropertyId.get(String(conn.propertyId))
      const isParentDisabled = parent?.status === 0
      const isAirbnbSyncPaused = this.isAirbnbSyncPaused(conn)

      const rentalAccounts = conn.rentalId ? rentalToAccounts.get(conn.rentalId) || [] : []
      const isDuplicate = rentalAccounts.length > 1
      const hasDisabledDuplicate = isDuplicate && rentalAccounts.some((a) => a.status === 0)

      // TV4-6008: a listing whose mapping was removed by an unlink keeps a
      // tombstone on the channel until it is relinked.
      const unlinked = !conn.rentalId
        ? unlinkedByProperty.get(String(conn.propertyId))?.get(String(conn.roomId))
        : undefined

      if (conn.roomMissing) {
        // TV4-6062: the mapped room was deleted on the channel side; the row exists so the
        // mapping is visible and removable. Pushes to it are paused/failing.
        warnings.set(conn.id, {
          level: 'danger',
          message: $localize`This room no longer exists on the channel, so this mapping cannot sync. Unlink the rental, or link the rental to a current room to re-map it.`,
        })
      } else if (isAirbnbSyncPaused) {
        warnings.set(conn.id, {
          level: 'warning',
          message: this.getAirbnbPausedMessage(conn),
        })
      } else if (unlinked) {
        const unlinkedDate = new Date((unlinked.unlinked_on || 0) * 1000).toLocaleDateString()
        warnings.set(conn.id, {
          level: 'danger',
          message:
            unlinked.origin === 'user'
              ? $localize`This listing was unlinked from AdvanceCM on ${unlinkedDate}. Bookings and calendar sync stopped — use Link to relink it to a rental.`
              : $localize`Airbnb unlinked this listing from AdvanceCM on ${unlinkedDate}. Bookings and calendar sync stopped — use Link to relink it to a rental.`,
        })
      } else if (isParentDisabled) {
        warnings.set(conn.id, {
          level: 'danger',
          message: this.isAirBnBV2
            ? $localize`This Airbnb host is disconnected — rates and availability are not syncing for this mapping. Reconnect or remove it from the Accounts tab.`
            : $localize`This account is disconnected — rates and availability are not syncing for this mapping. Reconnect or remove it from the Accounts tab.`,
        })
      } else if (hasDisabledDuplicate) {
        warnings.set(conn.id, {
          level: 'warning',
          message: this.isAirBnBV2
            ? $localize`This rental is also mapped to a disconnected Airbnb host. The active mapping is syncing fine, but the stale one should be cleaned up from the Accounts tab.`
            : $localize`This rental is also mapped to a disconnected account on this channel. The active mapping is syncing fine, but the stale one should be cleaned up from the Accounts tab.`,
        })
      }
    }

    return warnings
  }

  private computeMultiMappingRentalIds(connections: ConnectionView[]): Set<string> {
    const counts = new Map<string, number>()
    for (const conn of connections) {
      if (!conn.rentalId) continue
      counts.set(conn.rentalId, (counts.get(conn.rentalId) || 0) + 1)
    }
    const multi = new Set<string>()
    for (const [rid, count] of counts) {
      if (count >= 2) multi.add(rid)
    }
    return multi
  }

  isAirbnbSyncPaused(connection: ConnectionView) {
    return (
      this.isAirBnBV2 &&
      (!!connection?.syncPaused || connection?.airbnbListed === false || this.isAirbnbDeleted(connection))
    )
  }

  isAirbnbDeleted(connection: ConnectionView) {
    return this.isAirBnBV2 && (!!connection?.airbnbDeleted || connection?.syncPausedReason === 'deleted_on_airbnb')
  }

  private getAirbnbPausedMessage(connection: ConnectionView) {
    if (this.isAirbnbDeleted(connection)) {
      return $localize`This listing was deleted on Airbnb. AdvanceCM has paused Airbnb API sync for this mapping.`
    }
    if (connection?.syncPausedReason === 'unlisted_on_airbnb') {
      return $localize`This listing is unlisted on Airbnb. AdvanceCM has paused Airbnb API sync for this mapping.`
    }
    return $localize`This listing is no longer returned as active by Airbnb. AdvanceCM has paused Airbnb API sync for this mapping.`
  }

  private warnAirbnbSyncPaused(connection: ConnectionView) {
    if (!this.isAirbnbSyncPaused(connection)) return false
    this.toaster.warning(this.getAirbnbPausedMessage(connection))
    return true
  }

  private filterAirbnbSyncable(connections: ConnectionView[]) {
    const syncable = connections.filter((connection) => !this.isAirbnbSyncPaused(connection))
    if (connections.length !== syncable.length) {
      this.toaster.warning($localize`Paused Airbnb listings were skipped because API sync is disabled for them.`)
    }
    return syncable
  }

  onClearFilters() {
    this.filters.reset()
    this.onSearch('')
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onRefresh() {
    this.store.dispatch(LoadConnectionsByChannel({ channelName: this.channel.name, channelId: this.channel.id }))
  }

  filterData(filters: { linkage: string; propertyId: string; status: string }) {
    this.dataSource.data = R.pipe(
      R.filter((c: ConnectionView) => {
        return (
          !filters.linkage ||
          (c.rentalId && filters.linkage === 'linked') ||
          (!c.rentalId && filters.linkage === 'unlinked')
        )
      }),
      R.filter((c: ConnectionView) => {
        return !filters.propertyId || filters.propertyId == c.propertyId
      }),
      R.filter((c: ConnectionView) => {
        return (
          !filters.status ||
          (filters.status === 'paused' && this.isAirbnbSyncPaused(c)) ||
          filters.status === c.airbnbStatus
        )
      })
    )(this.connections)
  }

  onDetail(connection: ConnectionView, wizard = true) {
    this.connectionDialog.openSide(connection, wizard)
  }

  onMasterToggle(checked: boolean) {
    if (checked) {
      this.masterToggle(false)
    } else {
      this.unselectAll()
    }
  }

  onRentalDetail(connection: ConnectionView) {
    this.store.dispatch(OpenRentalOverlay({ rental: connection.rental }))
  }

  onLink(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return
    this.connectionHelperService.linkRental(this.channel.name, connection).subscribe()
  }

  onUnLink(connection: ConnectionView) {
    this.connectionHelperService.unlinkRental(this.channel.name, connection)
  }

  onUnlinkRentals() {
    const selected = this.getSelected().filter((t) => t.isApi && !t.isFeedApi && t.rentalId)
    if (selected.length < 1) {
      this.toaster.warning($localize`Please select at least one linked connection.`)
      return
    }
    this.connectionHelperService.unlinkRentals(selected)
    this.unselectAll()
  }

  onPushRates(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return
    if (this.isTiketLikeChannel) {
      this.connectionHelperService.pushRateForTiketLikeChannel(connection, this.channel, connection.rental).subscribe()
    } else {
      this.connectionHelperService.pushRatesForAPIConnection(this.channel, connection.rental, connection).subscribe()
    }
  }

  onPushAvailability(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return
    if (this.isTiketLikeChannel) {
      this.connectionHelperService.pushAvailabilityForTiketLike(connection, this.channel.name).subscribe()
    } else {
      this.connectionHelperService.pushAvailability(this.channel.name, connection).subscribe()
    }
  }

  onImportBookings(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return
    this.connectionHelperService.importBookings(this.channel.name, connection).subscribe()
  }

  onImportProperty(connection: ConnectionView) {
    if (this.warnAirbnbSyncPaused(connection)) return
    this.airbnbHelperService.importRoom(this.channel.name, connection).subscribe()
  }

  onSetVisibility(connection: ConnectionView) {
    this.airbnbSetVisibilityDialog.open(connection)
  }

  onCreateListDetails() {
    this.propertyContentHelperService.createProperty(this.channel).subscribe()
  }

  onRefreshRoomList(silent = false) {
    this.store.dispatch(
      RefreshConnectionsForChannel({ channelName: this.channel.name, channelId: this.channel.id, silent })
    )
  }

  onCreateAirbnbListing() {
    this.createAirbnbListingDialog.open(this.channel.id)
  }

  onPushAllAvailability() {
    const connections = this.filterAirbnbSyncable(this.getSelected())
    if (connections.length < 1) {
      this.toaster.warning($localize`Please select at least one active connection.`)
      return
    }
    this.connectionHelperService.pushAllAvailability(this.channel.name, connections)
  }

  onImportAllBookings() {
    const connections = this.filterAirbnbSyncable(this.getSelected())
    if (connections.length < 1) {
      this.toaster.warning($localize`Please select at least one active connection.`)
      return
    }
    this.connectionHelperService.importAllBookings(this.channel.name, connections)
  }

  onImportProperties() {
    const connections = this.filterAirbnbSyncable(this.getSelected().filter((c) => !c.rentalId))
    if (connections.length < 1) {
      this.toaster.warning($localize`Please select at least one unlinked connection.`)
      return
    }

    this.airbnbHelperService.importRooms(connections).subscribe(() => {
      this.unselectAll()
    })
  }
}
